import { useCallback, useMemo, type ComponentProps, type MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import tripPlannerGlossaryMessages from 'tripPlanner/i18n/tripPlannerGlossaryMessages'
import AssistChip from 'LuxKit/Chips/AssistChip'
import TextButton from 'LuxKit/Button/TextButton'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import type { TripItineraryData, TripStop } from 'tripPlanner/api/types'
import useAddHotelToTrip from './useAddHotelToTrip'
import type { SendMessageOptions } from './useAddHotelToTrip'
import { showTripActionPendingSnackbar } from 'tripPlanner/components/Snackbar/tripActionSnackbar'
import type { HotelRoomOption } from 'tripPlanner/types/hotelRoomOption'
import type { AssistantOccupancyAgeThresholds } from 'tripPlanner/assistant/utils/assistantOccupancyAgeThresholds'
import AddHotelToTripModal from './AddHotelToTripModal'
import noop from 'lib/function/noop'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { findOfferItemInItinerary } from 'tripPlanner/utils/chatItinerary'
import { useItineraryContextOptional } from 'tripPlanner/assistant/contexts/ItineraryContext'
import { useTripPlannerEventBusOptional } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { buildItemDeletedActivityEntry } from 'tripPlanner/assistant/surfaceSnapshots/buildItemDeletedActivityEntry'

interface Props {
  itineraryData?: TripItineraryData
  hotelName: string
  offerId: string
  offerType?: string
  hotelCity?: string
  contextStop?: TripStop
  onSendMessage: (message: string, options?: SendMessageOptions) => void
  variant?: 'filled' | 'outline'
  size?: 'small' | 'medium'
  fit?: 'auto' | 'full-width'
  /** Button kind used by the `fit` (TextButton) rendering only. */
  kind?: ComponentProps<typeof TextButton>['kind']
  /**
   * Overrides the CTA copy, for surfaces whose design calls the action something
   * else. `add` covers the hotel not being in the trip yet, `select`/`selected`
   * the room-option states once it is.
   */
  ctaLabels?: { add?: string; select?: string; selected?: string }
  /** Drops the leading plus/check glyph, for designs whose CTA is label-only. */
  hideActionIcons?: boolean
  onAfterAction?: () => void
  filterNights?: number
  /** Offer minimum length of stay; overrides Redux-derived min when provided. */
  minNights?: number
  checkIn?: string
  checkOut?: string
  roomOption?: HotelRoomOption
  selectRoomOptionWhenAdded?: boolean
  isRoomOptionSelected?: boolean
  /** External gate (e.g. charter bundle flight price not yet resolved/bookable). */
  disabled?: boolean
  occupancyAgeThresholds?: AssistantOccupancyAgeThresholds
}

function AddHotelToTripButton({
  itineraryData,
  hotelName,
  offerId,
  offerType,
  hotelCity,
  contextStop,
  onSendMessage,
  variant = 'filled',
  size = 'small',
  fit,
  kind = 'secondary',
  ctaLabels,
  hideActionIcons = false,
  onAfterAction = noop,
  filterNights,
  minNights,
  checkIn,
  checkOut,
  roomOption,
  selectRoomOptionWhenAdded = false,
  isRoomOptionSelected = false,
  disabled = false,
  occupancyAgeThresholds,
}: Props) {
  const intl = useIntl()
  const { isStreaming } = useChatStreamStatus()
  const itineraryContext = useItineraryContextOptional()
  const eventBus = useTripPlannerEventBusOptional()
  const isManualItineraryDeletePending = itineraryContext?.isManualItineraryDeletePending ?? false
  const isItineraryMutationPending = isManualItineraryDeletePending ||
    (itineraryContext?.isManualItineraryMovePending ?? false) ||
    (itineraryContext?.isFlightCartMutationPending ?? false)
  const {
    handleAddToTrip,
    handleSelectRoomOption,
    isModalOpen,
    closeModal,
    itineraryStatus,
    relevantStop,
    defaultDayNumber,
    defaultNights,
    minNights: resolvedMinNights,
    bookingRoomOption,
  } = useAddHotelToTrip({
    itineraryData,
    hotelName,
    offerId,
    offerType,
    hotelCity,
    contextStop,
    onSendMessage,
    onAfterAction,
    filterNights,
    minNights,
    checkIn,
    checkOut,
    roomOption,
    occupancyAgeThresholds,
  })

  const isAdded = itineraryStatus.type !== 'not-added'
  const itineraryItem = useMemo(() => itineraryData ? findOfferItemInItinerary(offerId, itineraryData) : undefined, [itineraryData, offerId])
  const shouldSelectRoomOption = isAdded && (selectRoomOptionWhenAdded || !!roomOption)
  const shouldShowSelectedRoomOption = shouldSelectRoomOption && isRoomOptionSelected

  const addToTripLabel = ctaLabels?.add ?? intl.formatMessage(tripPlannerGlossaryMessages.addToTrip)
  const selectLabel = ctaLabels?.select ?? intl.formatMessage({
    id: 'n07_aCaZJpD6d2i0nsYg',
    defaultMessage: 'Add to trip',
    description: 'Button label to add a hotel room package to the trip planner itinerary',
  })
  const selectedLabel = ctaLabels?.selected ?? intl.formatMessage({
    id: 'bVIhacFTytHhsK8sQjdK',
    defaultMessage: 'Already in trip',
    description: 'Disabled button label for a hotel room package already added to the trip planner itinerary',
  })

  const handleClick = useCallback((e: MouseEvent) => {
    e.stopPropagation()
    if (shouldShowSelectedRoomOption) return
    if (shouldSelectRoomOption) {
      handleSelectRoomOption()
      return
    }
    handleAddToTrip()
  }, [handleAddToTrip, handleSelectRoomOption, shouldSelectRoomOption, shouldShowSelectedRoomOption])

  const handleModalSubmit = useCallback((message: string) => {
    showTripActionPendingSnackbar(intl, 'add', hotelName)
    onSendMessage(message, { offerId, offerType, roomOption: bookingRoomOption, occupancyAgeThresholds })
    closeModal()
    onAfterAction()
  }, [intl, hotelName, onSendMessage, offerId, offerType, bookingRoomOption, occupancyAgeThresholds, closeModal, onAfterAction])

  const handleModalRemove = useCallback(async() => {
    if (!itineraryItem || isManualItineraryDeletePending) return
    const occurredAt = new Date().toISOString()
    if (!itineraryContext) return
    const deleted = await itineraryContext.deleteManualItineraryTarget({ targetType: 'item', itemId: itineraryItem.id }, itineraryItem.name)
    if (!deleted) return

    eventBus?.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildItemDeletedActivityEntry(itineraryItem.name, 'trip_planner.add_hotel_modal.remove_item', occurredAt)],
    })
    closeModal()
    onAfterAction()
  }, [closeModal, eventBus, isManualItineraryDeletePending, itineraryContext, itineraryItem, onAfterAction])

  const buttonLabel = shouldShowSelectedRoomOption ? selectedLabel : shouldSelectRoomOption ? selectLabel : isAdded ? itineraryStatus.label : addToTripLabel
  const defaultStartIcon = shouldShowSelectedRoomOption ? <LineCheckIcon /> : !isAdded || shouldSelectRoomOption ? <LinePlusIcon /> : undefined
  const startIcon = hideActionIcons ? undefined : defaultStartIcon
  // Decorative glyphs are what `hideActionIcons` drops; this chevron is not one — it is the
  // only signal that the button opens the add/update modal rather than acting immediately.
  const endIcon = isAdded && !shouldSelectRoomOption ? <LineAngleDownIcon /> : undefined
  const isDisabled = isStreaming || shouldShowSelectedRoomOption || disabled

  const trigger = fit ? (
    <TextButton kind={kind} size={size} fit={fit === 'full-width' ? 'full-width' : undefined} startIcon={startIcon} endIcon={endIcon} onClick={handleClick} disabled={isDisabled}>
      {buttonLabel}
    </TextButton>
  ) : (
    <AssistChip variant={variant} size={size} startIcon={startIcon} endIcon={endIcon} onClick={handleClick} disabled={isDisabled}>
      {buttonLabel}
    </AssistChip>
  )

  return (
    <>
      {trigger}
      {itineraryData && (
        <AddHotelToTripModal
          isOpen={isModalOpen}
          hotelName={hotelName}
          relevantStop={relevantStop}
          defaultDayNumber={defaultDayNumber}
          defaultNights={defaultNights}
          minNights={resolvedMinNights}
          mode={isAdded ? 'update' : 'add'}
          isMutationPending={isItineraryMutationPending}
          onClose={closeModal}
          onSubmit={handleModalSubmit}
          onRemove={itineraryItem && itineraryContext ? handleModalRemove : undefined}
        />
      )}
    </>
  )
}

export default AddHotelToTripButton
